import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { ExamAttempt, Question, Choice } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { AlertTriangle, Save, CheckCircle, WifiOff, Monitor } from 'lucide-react'

interface QuestionWithChoices extends Question {
  choices: Choice[]
}

interface Answer {
  question_id: string
  answer_text?: string
  selected_choice_ids?: string[]
}

const AUTOSAVE_DELAY = 3000 // 3秒
const STORAGE_KEY_PREFIX = 'exam_answers_'

export function ExamAttemptPage() {
  useDocumentTitle('試験受験中')
  const { attemptId } = useParams<{ attemptId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null)
  const [questions, setQuestions] = useState<QuestionWithChoices[]>([])
  const [answers, setAnswers] = useState<Record<string, Answer>>({})
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)

  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!user || !attemptId) return
    fetchExamAttempt()

    // オンライン/オフライン検知
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current)
      }
    }
  }, [attemptId, user])

  const fetchExamAttempt = async () => {
    if (!attemptId || !user) return

    try {
      setLoading(true)
      setError(null)

      // 試験attempt情報を取得
      const { data: attemptData, error: attemptError } = await supabase
        .from('exam_attempts')
        .select('*')
        .eq('attempt_id', attemptId)
        .eq('user_id', user.id)
        .single()

      if (attemptError || !attemptData) {
        setError('試験が見つかりませんでした')
        setLoading(false)
        return
      }

      if (attemptData.status !== 'in_progress') {
        setError('この試験は既に終了しています')
        setLoading(false)
        return
      }

      // 制限時間チェック
      const endsAt = new Date(attemptData.ends_at)
      const now = new Date()
      if (endsAt < now) {
        // 時間切れ - 自動提出
        await handleAutoSubmit(attemptData.attempt_id)
        return
      }

      setAttempt(attemptData)

      // 問題を取得
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select('*, choices(*)')
        .eq('exam_id', attemptData.exam_id)
        .order('sort_order', { ascending: true })

      if (questionsError) throw questionsError

      const questionsWithChoices = (questionsData || []).map((q: any) => ({
        ...q,
        choices: (q.choices || []).sort(
          (a: Choice, b: Choice) => a.sort_order - b.sort_order
        ),
      }))

      setQuestions(questionsWithChoices)

      // 既存の回答を取得
      const { data: answersData } = await supabase
        .from('attempt_answers')
        .select('*')
        .eq('attempt_id', attemptId)

      // localStorageから回答を復元（最新の状態を優先）
      const storageKey = STORAGE_KEY_PREFIX + attemptId
      const cachedAnswers = localStorage.getItem(storageKey)
      let initialAnswers: Record<string, Answer> = {}

      if (cachedAnswers) {
        try {
          initialAnswers = JSON.parse(cachedAnswers)
        } catch (e) {
          console.error('Failed to parse cached answers:', e)
        }
      }

      // DBの回答をマージ（localStorageの方が新しい可能性があるのでDBを先に）
      if (answersData) {
        answersData.forEach((ans) => {
          if (!initialAnswers[ans.question_id]) {
            initialAnswers[ans.question_id] = {
              question_id: ans.question_id,
              answer_text: ans.answer_text,
              selected_choice_ids: ans.selected_choice_ids,
            }
          }
        })
      }

      setAnswers(initialAnswers)

      // タイマー開始
      startTimer(endsAt)
    } catch (err: any) {
      console.error('Error fetching exam attempt:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const startTimer = (endsAt: Date) => {
    const updateTimer = () => {
      const now = new Date()
      const remaining = Math.max(0, endsAt.getTime() - now.getTime())
      setTimeRemaining(remaining)

      if (remaining === 0) {
        // 時間切れ
        handleAutoSubmit(attemptId!)
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }

  const handleAnswerChange = useCallback(
    (questionId: string, answerData: Partial<Answer>) => {
      setAnswers((prev) => {
        const updated = {
          ...prev,
          [questionId]: {
            ...prev[questionId],
            ...answerData,
            question_id: questionId,
          },
        }

        // localStorageに保存
        const storageKey = STORAGE_KEY_PREFIX + attemptId
        localStorage.setItem(storageKey, JSON.stringify(updated))

        return updated
      })

      // 自動保存をスケジュール
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
      autosaveTimerRef.current = setTimeout(() => {
        saveAnswers()
      }, AUTOSAVE_DELAY)
    },
    [attemptId]
  )

  const saveAnswers = async () => {
    if (!attemptId || !user || !isOnline) return

    try {
      setSaving(true)

      const bffUrl = import.meta.env.VITE_BFF_URL
      const token = (await supabase.auth.getSession()).data.session?.access_token

      if (!token) return

      // 各回答を保存
      for (const answer of Object.values(answers)) {
        await fetch(`${bffUrl}/exam-answer`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            attempt_id: attemptId,
            question_id: answer.question_id,
            answer_text: answer.answer_text,
            selected_choice_ids: answer.selected_choice_ids,
          }),
        })
      }

      setLastSaved(new Date())
    } catch (err: any) {
      console.error('Error saving answers:', err)
      // エラーは表示しない（自動保存のため）
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    if (!attemptId || !user) return

    const confirmed = window.confirm(
      '試験を終了してもよろしいですか？\n終了後は回答を変更できません。'
    )
    if (!confirmed) return

    try {
      setSubmitting(true)
      setError(null)

      // 最後に保存
      await saveAnswers()

      const bffUrl = import.meta.env.VITE_BFF_URL
      const token = (await supabase.auth.getSession()).data.session?.access_token

      if (!token) {
        throw new Error('認証トークンが取得できませんでした')
      }

      const response = await fetch(`${bffUrl}/exam-submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          attempt_id: attemptId,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || '試験の提出に失敗しました')
      }

      // localStorageをクリア
      const storageKey = STORAGE_KEY_PREFIX + attemptId
      localStorage.removeItem(storageKey)

      // 結果ページへ遷移
      navigate(`/exams/result/${attemptId}`)
    } catch (err: any) {
      console.error('Error submitting exam:', err)
      setError(err.message || '試験の提出に失敗しました')
      setSubmitting(false)
    }
  }

  const handleAutoSubmit = async (attemptId: string) => {
    try {
      // 自動保存
      await saveAnswers()

      const bffUrl = import.meta.env.VITE_BFF_URL
      const token = (await supabase.auth.getSession()).data.session?.access_token

      if (!token) return

      await fetch(`${bffUrl}/exam-submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          attempt_id: attemptId,
          is_timeout: true,
        }),
      })

      // localStorageをクリア
      const storageKey = STORAGE_KEY_PREFIX + attemptId
      localStorage.removeItem(storageKey)

      // 結果ページへ遷移
      navigate(`/exams/result/${attemptId}`, {
        state: { timeout: true },
      })
    } catch (err) {
      console.error('Error auto-submitting exam:', err)
    }
  }

  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const answeredCount = Object.keys(answers).length
  const totalCount = questions.length
  const progress = totalCount > 0 ? (answeredCount / totalCount) * 100 : 0

  if (loading) {
    return (
      <MainLayout>
        <Loading message="試験を読み込み中..." />
      </MainLayout>
    )
  }

  if (error || !attempt) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error || '試験が見つかりませんでした'} />
        </div>
      </MainLayout>
    )
  }

  const isTimeLow = timeRemaining !== null && timeRemaining < 5 * 60 * 1000 // 5分未満

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Sticky Exam Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-semibold text-gray-900 truncate max-w-[200px] sm:max-w-md">
              {attempt.exam_id}
            </h1>
            <div className="h-4 w-px bg-gray-300 mx-2 hidden sm:block"></div>
            <div className="hidden sm:flex items-center text-sm text-gray-500 gap-2">
              <Monitor size={16} />
              <span>オンライン試験</span>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              {!isOnline && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <WifiOff className="h-4 w-4" />
                  <span className="hidden sm:inline">オフライン</span>
                </div>
              )}

              {isOnline && saving && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Save className="h-4 w-4 animate-pulse" />
                  <span className="hidden sm:inline">保存中</span>
                </div>
              )}

              {isOnline && !saving && lastSaved && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">保存済み</span>
                </div>
              )}
            </div>

            <div className="text-right">
              <div
                className={`font-mono text-xl font-medium tabular-nums ${isTimeLow ? 'text-red-600' : 'text-gray-900'}`}
              >
                {timeRemaining !== null ? formatTime(timeRemaining) : '--:--'}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
                残り時間
              </div>
            </div>
          </div>
        </div>
        {/* Progress Bar */}
        <div className="h-1 w-full bg-gray-100">
          <div
            className="h-full bg-[#006cc6] transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full p-6 md:p-8">
        {/* 時間切れ警告 */}
        {isTimeLow && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-red-100">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
              <div className="text-sm text-red-800 flex-1">
                <p className="font-semibold">残り時間が少なくなっています</p>
                <p className="text-xs mt-0.5">制限時間を過ぎると自動的に提出されます</p>
              </div>
            </div>
          </div>
        )}

        {/* 問題一覧 */}
        {questions.map((question, index) => (
          <div key={question.question_id}>
            <div id={`question-${question.question_id}`} className="p-8 flex flex-col scroll-mt-24">
              <div className="mb-4">
                <div className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                  問題 {index + 1} <span className="text-gray-300">•</span>{' '}
                  <span className="text-gray-500">{question.points}点</span>
                </div>
              </div>

              <h2 className="text-xl font-medium text-gray-900 mb-6 leading-relaxed">
                {question.question_text}
              </h2>

              {/* 単一選択 */}
              {question.question_type === 'single_choice' && (
                <div className="space-y-1">
                  {question.choices.map((choice) => {
                    const isSelected =
                      answers[question.question_id]?.selected_choice_ids?.[0] ===
                      choice.choice_id
                    return (
                      <button
                        key={choice.choice_id}
                        onClick={() =>
                          handleAnswerChange(question.question_id, {
                            selected_choice_ids: [choice.choice_id],
                          })
                        }
                        className="w-full text-left py-3 px-3 rounded-lg transition-colors duration-200 flex items-start group hover:bg-gray-50"
                      >
                        <div
                          className={`w-5 h-5 rounded-full border mt-0.5 mr-4 flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected
                              ? 'border-[#006cc6] bg-[#006cc6]'
                              : 'border-gray-300 group-hover:border-gray-400'
                          }`}
                        >
                          {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <span
                          className={`text-base ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}
                        >
                          {choice.choice_text}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}

              {/* 複数選択 */}
              {question.question_type === 'multiple_choice' && (
                <div className="space-y-1">
                  {question.choices.map((choice) => {
                    const isSelected =
                      answers[question.question_id]?.selected_choice_ids?.includes(
                        choice.choice_id
                      ) || false
                    return (
                      <button
                        key={choice.choice_id}
                        onClick={() => {
                          const currentIds =
                            answers[question.question_id]?.selected_choice_ids || []
                          const newIds = isSelected
                            ? currentIds.filter((id) => id !== choice.choice_id)
                            : [...currentIds, choice.choice_id]
                          handleAnswerChange(question.question_id, {
                            selected_choice_ids: newIds,
                          })
                        }}
                        className="w-full text-left py-3 px-3 rounded-lg transition-colors duration-200 flex items-start group hover:bg-gray-50"
                      >
                        <div
                          className={`w-5 h-5 rounded border mt-0.5 mr-4 flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected
                              ? 'border-[#006cc6] bg-[#006cc6]'
                              : 'border-gray-300 group-hover:border-gray-400'
                          }`}
                        >
                          {isSelected && (
                            <CheckCircle className="w-3 h-3 text-white" strokeWidth={3} />
                          )}
                        </div>
                        <span
                          className={`text-base ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}
                        >
                          {choice.choice_text}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}

              {/* 記述式 */}
              {question.question_type === 'text' && (
                <textarea
                  value={answers[question.question_id]?.answer_text || ''}
                  onChange={(e) =>
                    handleAnswerChange(question.question_id, {
                      answer_text: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006cc6] focus:border-transparent transition-colors"
                  rows={5}
                  placeholder="回答を入力してください"
                />
              )}
            </div>

            {index < questions.length - 1 && (
              <div className="flex justify-center py-8">
                <div className="w-[80%] md:w-[40%] h-px bg-gray-300"></div>
              </div>
            )}
          </div>
        ))}

        {/* 提出ボタン（下部） */}
        <div className="pt-20 pb-12">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full text-lg h-14 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors shadow-lg shadow-green-500/20"
          >
            {submitting ? '提出中...' : '試験を提出する'}
          </button>
          <p className="text-center text-sm text-gray-500 mt-4">
            全ての問題に回答したら、試験を提出してください
          </p>
        </div>
      </main>
    </div>
  )
}
